import { useAuthStore } from "@/store/authStore";
import { setSentryUser } from "@/lib/sentry";

/**
 * Keeps Sentry's idea of who is using the app in step with the auth store.
 *
 * A subscription rather than a call inside the store's `login`/`logout` actions:
 * the store is a plain state container with its own tests, and the session it
 * holds is restored from `localStorage` on load without any action running at
 * all. Subscribing catches every path into and out of a session — sign-in,
 * sign-out, school switch, impersonation start and stop, and the rehydrate on a
 * reload — without the store having to know Sentry exists.
 *
 * What is sent is an opaque id plus role and school as tags. Never an email or a
 * name; `setSentryUser` explains why.
 */
function report(state: ReturnType<typeof useAuthStore.getState>): void {
  const { user, impersonation, support } = state;

  if (!user) {
    setSentryUser(null);
    return;
  }

  setSentryUser({
    id: user.id,
    // The offices they are *acting as*, which are what decided what the app
    // rendered and therefore what an error means.
    roles: impersonation?.effectiveRoles ?? user.roles,
    schoolId: user.schoolId,
    impersonating: impersonation?.isImpersonating,
    // Without this, a crash an operator hit while reproducing a ticket is
    // indistinguishable from one the account's owner hit themselves — same id,
    // same offices, same school, because a support session *is* that account.
    // Which of the two it was is the first thing anyone reading the issue needs
    // to know, and the only place the client can learn it is the store: the
    // claim rides the token, and this app does not read tokens.
    supportSession: support ? true : undefined,
  });
}

/** Call once, at startup. Returns the unsubscribe, which nothing needs today. */
export function syncSentryUserWithAuth(): () => void {
  report(useAuthStore.getState());
  return useAuthStore.subscribe(report);
}
